// Curator page vocabulary: the pieces every admin page is built from, so
// headers, cards, tables and tabs look the same everywhere. Styling only; no
// data fetching.
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  buttonVariants,
  cn,
  errorMessage,
} from '@remote-medium/ui';
import { ArrowLeft, ArrowUpRight, CircleAlert, CircleCheck, LoaderCircle } from 'lucide-react';
import { useState, type ComponentProps, type ReactNode } from 'react';
import { Link } from 'react-router';

export const CARD = 'rounded-xl border border-border bg-card shadow-sm';
export const H2 = 'm-0 font-sans text-base leading-6 font-semibold';
export const VIEW_ALL = 'text-xs font-medium text-foreground underline underline-offset-4';
/** Primary link inside a table or list: reads as text, underlines on hover. */
export const ROW_LINK = 'font-semibold text-foreground no-underline hover:underline';

export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-col gap-8', className)}>{children}</div>;
}

export function PageHeader({
  title,
  description,
  back,
  status,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  back?: { to: string; label: string };
  status?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-3">
      {back && (
        <Link
          to={back.to}
          className="inline-flex w-fit items-center gap-1.5 text-xs font-medium text-muted-foreground no-underline hover:text-foreground"
        >
          <ArrowLeft aria-hidden="true" className="size-3.5" />
          {back.label}
        </Link>
      )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="m-0 font-serif text-2xl font-medium tracking-tight">{title}</h1>
            {status}
          </div>
          {description && (
            // Prose is capped for line length; structured meta rows use the width.
            <div
              className={cn(
                'm-0 text-sm text-pretty text-muted-foreground',
                typeof description === 'string' && 'max-w-2xl',
              )}
            >
              {description}
            </div>
          )}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}

export function Card({
  className,
  children,
  ...rest
}: ComponentProps<'section'> & { children: ReactNode }) {
  return (
    <section className={cn(CARD, 'flex flex-col gap-4 p-5', className)} {...rest}>
      {children}
    </section>
  );
}

export function SectionHeader({
  title,
  aside,
  actions,
}: {
  title: ReactNode;
  aside?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className={H2}>{title}</h2>
        {aside && <span className="text-xs text-muted-foreground">{aside}</span>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Segmented control used for status/kind filters. */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  className,
}: {
  tabs: { value: T; label: string; count?: number | undefined }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn('flex w-fit max-w-full flex-wrap gap-0.5 rounded-lg bg-muted p-1', className)}
    >
      {tabs.map((t) => {
        const active = value === t.value;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.value)}
            className={cn(
              'rounded-md px-2.5 py-1 text-sm whitespace-nowrap transition-colors',
              active
                ? 'bg-card font-medium text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
            {t.count !== undefined && <span className="ml-1.5 tabular-nums">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Card-framed table. Children are plain <thead>/<tbody> markup; spacing,
 * header tone, row rules and hover come from here.
 */
export function DataTable({
  children,
  className,
  minWidth = 'min-w-3xl',
  framed = true,
}: {
  children: ReactNode;
  className?: string;
  minWidth?: string;
  /** False when the table sits inside a Card that already draws the frame. */
  framed?: boolean;
}) {
  return (
    // `relative` so absolutely positioned content (sr-only headers) is clipped
    // by this scroller instead of widening the page on narrow screens.
    <div className={cn(framed && CARD, 'relative overflow-x-auto', className)}>
      <table
        className={cn(
          'w-full border-collapse text-left text-sm',
          '[&_th]:px-3 [&_th]:py-3 [&_th]:text-xs [&_th]:font-medium [&_th]:whitespace-nowrap [&_th]:text-muted-foreground',
          '[&_td]:px-3 [&_td]:py-3.5 [&_td]:align-middle',
          '[&_td:first-child]:pl-5 [&_td:last-child]:pr-5 [&_th:first-child]:pl-5 [&_th:last-child]:pr-5',
          '[&_tbody_tr]:border-t [&_tbody_tr]:border-border [&_tbody_tr]:transition-colors',
          minWidth,
        )}
      >
        {children}
      </table>
    </div>
  );
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="py-8! text-muted-foreground">
        {children}
      </td>
    </tr>
  );
}

/**
 * Loading and error states as a single table row, so the header stays in
 * place while data loads. Render data rows only when `tableReady` is true.
 */
export function StatusRow({
  colSpan,
  loading,
  error,
}: {
  colSpan: number;
  loading: boolean;
  error: unknown;
}) {
  if (loading)
    return (
      <tr>
        <td colSpan={colSpan} className="py-12!">
          <span
            role="status"
            className="flex items-center justify-center gap-2 text-sm text-muted-foreground"
          >
            <LoaderCircle aria-hidden="true" className="size-4 motion-safe:animate-spin" />
            Loading…
          </span>
        </td>
      </tr>
    );
  if (error)
    return (
      <tr>
        <td colSpan={colSpan} className="py-8! text-destructive" role="alert">
          {errorMessage(error)}
        </td>
      </tr>
    );
  return null;
}

/** True when a table should render its data rows (loaded, no error). */
export const tableReady = <T,>(q: { data: T | null; loading: boolean; error: unknown }) =>
  !q.loading && !q.error && q.data !== null;

/** Label/value list with hairline rules, like the Overview count cards. */
export function Facts({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="m-0 flex flex-col">
      {items.map(([k, v], i) => (
        <div
          key={i}
          className="grid grid-cols-1 gap-1 border-t border-border py-2.5 text-sm first:border-t-0 first:pt-0 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4"
        >
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="m-0 min-w-0 text-foreground">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'error';
  children: ReactNode;
}) {
  const Icon = tone === 'error' ? CircleAlert : CircleCheck;
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm',
        tone === 'error'
          ? 'border-destructive/30 bg-destructive/5 text-destructive'
          : 'border-border bg-card text-foreground',
      )}
    >
      <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function None({ children = 'None' }: { children?: ReactNode }) {
  return <span className="text-muted-foreground">{children}</span>;
}

export function Chips({ items, max }: { items: string[]; max?: number }) {
  if (!items.length) return <None>None listed</None>;
  const shown = max ? items.slice(0, max) : items;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((s) => (
        <span
          key={s}
          className="inline-flex h-5 items-center rounded-sm bg-secondary px-2 text-xs whitespace-nowrap text-secondary-foreground"
        >
          {s}
        </span>
      ))}
      {max && items.length > max && (
        <span className="text-xs text-muted-foreground">+{items.length - max}</span>
      )}
    </div>
  );
}

export function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-foreground underline-offset-4 hover:underline"
    >
      {children}
      <ArrowUpRight aria-hidden="true" className="size-3.5 text-muted-foreground" />
    </a>
  );
}

export function LinkButton({
  to,
  variant = 'outline',
  size = 'sm',
  children,
}: {
  to: string;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
  size?: 'sm' | 'default';
  children: ReactNode;
}) {
  return (
    <Link to={to} className={cn(buttonVariants({ variant, size }), 'no-underline')}>
      {children}
    </Link>
  );
}

/**
 * Button that asks first. Replaces window.confirm: says what will happen,
 * keeps the dialog open on failure and shows the error inside it.
 */
export function ConfirmButton({
  children,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  variant = 'outline',
  size = 'sm',
  disabled,
  onConfirm,
}: {
  children: ReactNode;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'destructive';
  size?: 'sm' | 'default';
  disabled?: boolean;
  onConfirm: () => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      setOpen(false);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        setOpen(next);
        setError(null);
      }}
    >
      <DialogTrigger render={<Button variant={variant} size={size} disabled={disabled} />}>
        {children}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {error && <p className="m-0 text-xs text-destructive">{error}</p>}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" disabled={busy} />}>
            {cancelLabel}
          </DialogClose>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            disabled={busy}
            onClick={() => void confirm()}
          >
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
